<template>
  <div class="calendar-app poppins" v-if="isCalendarShown">
    <!-- Navigation Bar -->
    <div
      class="d-flex justify-content-between align-items-center pb-3 border-bottom"
    >
      <div class="d-flex align-items-center">
        <button
          @click="handlePrev"
          class="btn btn-sm btn-outline-secondary mr-2"
        >
          <v-icon size="24">mdi-chevron-left</v-icon>
        </button>
        <button
          @click="handleNext"
          class="btn btn-sm btn-outline-secondary mr-2"
        >
          <v-icon size="24">mdi-chevron-right</v-icon>
        </button>
        <button @click="handleToday" class="btn btn-sm btn-outline-secondary">
          {{ $t("today") }}
        </button>
      </div>

      <h2 class="h5 font-weight-semibold mb-0">{{ currentDateTitle }}</h2>

      <div class="d-flex align-items-center" role="group">
        <button
          @click="() => changeView('dayGridMonth')"
          :class="[
            'btn btn-sm btn-outline-secondary me-3',
            { active: currentView === 'dayGridMonth' },
          ]"
        >
          {{ $t("month") }}
        </button>
        <button
          @click="() => changeView('timeGridWeek')"
          :class="[
            'btn btn-sm btn-outline-secondary me-3',
            { active: currentView === 'timeGridWeek' },
          ]"
        >
          {{ $t("week") }}
        </button>
        <button
          @click="() => changeView('timeGridDay')"
          :class="[
            'btn btn-sm btn-outline-secondary me-3',
            { active: currentView === 'timeGridDay' },
          ]"
        >
          {{ $t("daily") }}
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div
      v-if="loading"
      class="d-flex flex-column align-items-center justify-content-center py-5"
    >
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <p class="mt-3 text-muted">{{ $t("loading_calendar_data") }}</p>
    </div>

    <!-- Error State -->
    <div
      v-if="error"
      class="d-flex flex-column align-items-center justify-content-center py-5"
    >
      <v-icon color="error" size="48">mdi-alert-circle</v-icon>
      <p class="mt-3 text-muted">{{ calendarErrorMessage }}</p>
      <button @click="fetchCalendarData" class="btn btn-primary mt-3">
        {{ $t("retry") }}
      </button>
    </div>

    <!-- Calendar -->
    <div v-show="!loading && !error" class="calendar-wrapper">
      <FullCalendar
        v-if="calendarKey"
        :key="calendarKey"
        ref="fullCalendar"
        :options="calendarOptions"
      />

      <!-- Color Legend -->
      <div v-show="!loading && !error" class="calendar-legend">
        <div
          v-for="(item, index) in legendItems"
          :key="index"
          class="legend-item"
        >
          <span
            class="legend-dot"
            :style="{ backgroundColor: item.color }"
          ></span>
          <span class="legend-text">{{ legendLabel(item) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import ApiService from "@/core/services/api.service";

export default {
  name: "ASNCalendarView",
  components: {
    FullCalendar,
  },
  data() {
    return {
      legendItems: [],
      cachedData: {
        monthly: {},
        weekly: {},
        daily: {},
      }, // Updated cache structure for all views
      // cachedMonthData: {},
      // cachedWeekData: {},
      // cachedDayData: {},
      currentView: "dayGridMonth",
      currentDateTitle: "",
      isCalendarShown: false,
      showModal: false,
      isEditMode: false,
      selectedEvent: null,
      eventForm: {
        title: "",
        start: "",
        end: "",
        color: "#2196F3",
        allDay: false,
      },
      loading: false,
      error: null,
      calendarKey: 0,
      currentDate: new Date(),
      eventColors: [
        { name: "Blue", value: "#2196F3" },
        { name: "Green", value: "#4CAF50" },
        { name: "Purple", value: "#9C27B0" },
        { name: "Orange", value: "#FF9800" },
        { name: "Red", value: "#F44336" },
        { name: "Teal", value: "#009688" },
        { name: "Pink", value: "#E91E63" },
        { name: "Yellow", value: "#FFC107" },
      ],
      calendarOptions: {
        eventContent: this.renderEventContent,
        showNonCurrentDates: false,
        fixedWeekCount: false,
        scrollTime: "07:00:00",
        slotMinTime: "07:00:00",
        slotMaxTime: "20:00:00",
        slotDuration: "00:20:00",
        allDaySlot: false,
        dateClick: this.handleDateClick,
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
        initialView: "dayGridMonth",
        headerToolbar: false,
        height: "auto",
        editable: false,
        selectable: true,
        selectMirror: true,
        dayMaxEvents: 4,
        weekends: true,
        firstDay: 1,
        select: this.handleDateSelect,
        eventClick: this.handleEventClick,
        eventDrop: this.handleEventDrop,
        eventResize: this.handleEventResize,
        datesSet: this.handleDatesSet,
        // moreLinkText: (num) => `+${num} more`,
        events: [],
        // events: [
        //   {
        //     id: "1",
        //     title: "All Day Event",
        //     start: "2026-02-01",
        //     color: "#4CAF50",
        //     allDay: true,
        //   },
        // ],
      },
    };
  },
  // mounted() {
  //   this.fetchCalendarData();
  //   // console.log(window.innerWidth + " x " + window.innerHeight);
  //   // console.log(window.outerHeight - window.innerHeight);
  // },
  computed: {
    calendarErrorMessage() {
      if (!this.error) {
        return "";
      }
      return this.$te(this.error) ? this.$t(this.error) : this.error;
    },
  },
  methods: {
    legendLabel(item) {
      if (!item || !item.text) {
        return "";
      }
      return this.$te(item.text) ? this.$t(item.text) : item.text;
    },
    toggleCalendar() {
      if (!this.isCalendarShown) {
        this.fetchCalendarData();
      }
      this.isCalendarShown = !this.isCalendarShown;
    },
    handleToday() {
      const calendarApi = this.$refs.fullCalendar.getApi();
      calendarApi.today();
      this.currentDate = new Date();
      this.fetchCalendarData();
    },

    handlePrev() {
      const calendarApi = this.$refs.fullCalendar.getApi();
      calendarApi.prev();
      this.currentDate = calendarApi.getDate();
      this.fetchCalendarData();
    },

    handleNext() {
      const calendarApi = this.$refs.fullCalendar.getApi();
      calendarApi.next();
      this.currentDate = calendarApi.getDate();
      this.fetchCalendarData();
    },

    setupHeaderClickListeners() {
      this.$nextTick(() => {
        if (!this.$refs.fullCalendar) return;

        const calendarEl = this.$refs.fullCalendar.$el;

        // Handle weekly view headers
        if (this.currentView === "timeGridWeek") {
          const headers = calendarEl.querySelectorAll(".fc-col-header-cell");

          headers.forEach((header) => {
            header.style.cursor = "pointer";
            header.addEventListener("click", (e) => {
              const dateStr = header.getAttribute("data-date");
              if (dateStr) {
                this.currentDate = new Date(dateStr + "T00:00:00");

                const calendarApi = this.$refs.fullCalendar.getApi();
                calendarApi.changeView("timeGridDay");
                this.currentView = "timeGridDay";

                this.fetchCalendarData();
              }
            });
          });
        }

        // Remove monthly view header click handlers - no longer making them clickable
        if (this.currentView === "dayGridMonth") {
          const dayHeaders = calendarEl.querySelectorAll(".fc-col-header-cell");
          dayHeaders.forEach((header) => {
            header.style.cursor = "default";
          });
        }
      });
    },

    handleDateClick(info) {
      // Handle clicks in weekly view - switch to daily
      if (this.currentView === "timeGridWeek") {
        this.currentDate = info.date;

        const calendarApi = this.$refs.fullCalendar.getApi();
        calendarApi.changeView("timeGridDay");
        this.currentView = "timeGridDay";

        this.fetchCalendarData();
      }

      // Handle clicks in monthly view - switch to daily
      if (this.currentView === "dayGridMonth") {
        this.currentDate = info.date;

        const calendarApi = this.$refs.fullCalendar.getApi();
        calendarApi.changeView("timeGridDay");
        this.currentView = "timeGridDay";

        this.fetchCalendarData();
      }
    },

    handleEventClick(info) {
      // Prevent default behavior
      info.jsEvent.preventDefault();

      // Handle clicks in weekly view - switch to daily
      if (this.currentView === "timeGridWeek") {
        this.currentDate = info.event.start;

        const calendarApi = this.$refs.fullCalendar.getApi();
        calendarApi.changeView("timeGridDay");
        this.currentView = "timeGridDay";

        this.fetchCalendarData();
      }

      // Handle clicks in monthly view - switch to daily
      if (this.currentView === "dayGridMonth") {
        this.currentDate = info.event.start;

        const calendarApi = this.$refs.fullCalendar.getApi();
        calendarApi.changeView("timeGridDay");
        this.currentView = "timeGridDay";

        this.fetchCalendarData();
      }
    },

    formatDateForAPI(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const viewType = this.getViewType();

      // For monthly view, always use first day of month
      if (viewType === "monthly") {
        return `${year}-${month}-01`;
      }

      // For weekly view, get the start of the week (Monday)
      if (viewType === "weekly") {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
        d.setDate(diff);

        const weekYear = d.getFullYear();
        const weekMonth = String(d.getMonth() + 1).padStart(2, "0");
        const weekDay = String(d.getDate()).padStart(2, "0");
        return `${weekYear}-${weekMonth}-${weekDay}`;
      }

      // For daily view, use the actual date
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    getViewType() {
      if (this.currentView === "dayGridMonth") return "monthly";
      if (this.currentView === "timeGridWeek") return "weekly";
      if (this.currentView === "timeGridDay") return "daily";
      return "monthly";
    },

    async fetchCalendarData() {
      const formattedDate = this.formatDateForAPI(this.currentDate);
      const viewType = this.getViewType();

      let filters = [];
      if (this.$store.getters.getASNTableState?.filters?.length) {
        filters = this.$store.getters.getASNTableState.filters;
      }
      const cacheKey = `${formattedDate}_${JSON.stringify(filters)}`;

      // Check cache for current view type and date (including filters)
      if (this.cachedData[viewType][cacheKey]) {
        // console.log(`Using cached ${viewType} data for:`, cacheKey);
        this.calendarOptions.events = this.cachedData[viewType][cacheKey];
        const dateToGo = new Date(this.currentDate);
        this.calendarKey++;
        this.$nextTick(() => {
          if (this.$refs.fullCalendar) {
            const calendarApi = this.$refs.fullCalendar.getApi();
            calendarApi.gotoDate(dateToGo);
            calendarApi.changeView(this.currentView);
            calendarApi.updateSize();
            calendarApi.render();
            this.setupHeaderClickListeners(); // Add this line
          }
        });
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        const payload = {
          type: viewType,
          date: formattedDate,
        };

        if (filters && filters.length > 0) {
          payload.filters = filters;
        }

        await ApiService.post("/fulfillment/asn/calendar_view", payload)
          .then(({ data }) => {
            const events = this.transformDataToEvents(data.dates, viewType);

            // Update legend labels from API
            if (data.labels) {
              this.legendItems = data.labels;
            }

            // Cache the events for this view type, date and filters
            this.cachedData[viewType][cacheKey] = events;

            // Update events in calendarOptions
            this.calendarOptions.events = events;

            const dateToGo = new Date(this.currentDate);
            this.calendarKey++;
            this.loading = false;

            this.$nextTick(() => {
              if (this.$refs.fullCalendar) {
                const calendarApi = this.$refs.fullCalendar.getApi();
                calendarApi.gotoDate(dateToGo);
                calendarApi.changeView(this.currentView);
                calendarApi.updateSize();
                calendarApi.render();
                this.setupHeaderClickListeners(); // Add this line
              }
            });
          })
          .finally(() => {
            this.loading = false;
          });
      } catch (err) {
        // console.error("Error fetching calendar data:", err);
        this.error = "calendar_load_failed";
        this.loading = false;
      }
    },

    // Monthly data: each date has a flat `items` array (all-day events)
    transformDataToEvents(dates, viewType) {
      const events = [];

      if (viewType === "monthly") {
        // Monthly view - simple items
        dates.forEach((dateData) => {
          dateData.items.forEach((item, index) => {
            events.push({
              id: `${dateData.date}-${index}`,
              title: item.text,
              start: dateData.date,
              backgroundColor: item.bg_color,
              borderColor: item.bg_color,
              allDay: true,
            });
          });
        });
      } else {
        // Weekly and Daily views - time slots
        dates.forEach((dateData) => {
          if (dateData.time_slots && dateData.time_slots.length > 0) {
            dateData.time_slots.forEach((timeSlot, slotIndex) => {
              timeSlot.items.forEach((item, itemIndex) => {
                const startDateTime = `${dateData.date}T${timeSlot.from}:00`;
                const endDateTime = `${dateData.date}T${timeSlot.to}:00`;

                events.push({
                  id: `${dateData.date}-${slotIndex}-${itemIndex}`,
                  title: item.text.replace(/<br\/>/g, "\n"), // Convert HTML breaks to newlines
                  start: startDateTime,
                  end: endDateTime,
                  backgroundColor: item.bg_color,
                  borderColor: item.bg_color,
                  allDay: false,
                });
              });
            });
          }
        });
      }

      return events;
    },

    // Weekly / Daily data: each date has `time_slots`, each slot has `items`
    transformTimeSlotEvents(dates) {
      const events = [];

      dates.forEach((dateData) => {
        (dateData.time_slots || []).forEach((slot, slotIndex) => {
          (slot.items || []).forEach((item, itemIndex) => {
            events.push({
              id: `${dateData.date}-${slotIndex}-${itemIndex}`,
              title: item.text,
              start: `${dateData.date}T${slot.from}:00`,
              end: `${dateData.date}T${slot.to}:00`,
              backgroundColor: item.bg_color,
              borderColor: item.bg_color,
              allDay: false,
              editable: false,
            });
          });
        });
      });

      return events;
    },

    renderEventContent(arg) {
      const container = document.createElement("div");
      container.className = "custom-event";
      container.style.backgroundColor = arg.event.backgroundColor;

      if (arg.event.allDay) {
        // Monthly view styling
        container.style.padding = "3px 6px";
        container.style.margin = "1px 0";
        container.style.borderRadius = "3px";
        container.style.fontSize = "12px";
        container.style.whiteSpace = "nowrap";
      } else {
        // Weekly/Daily view styling
        // container.style.padding = "6px 8px";
        container.style.margin = "0";
        container.style.borderRadius = "0";
        container.style.fontSize = "13px";
        container.style.whiteSpace = "pre-wrap";
        container.style.height = "100%";
        container.style.width = "100%";
        container.style.boxSizing = "border-box";
      }

      container.style.color = "white";
      container.style.fontWeight = "500";
      container.style.overflow = "hidden";
      container.style.textOverflow = "ellipsis";
      container.style.lineHeight = "1.3";

      container.textContent = arg.event.title;

      return { domNodes: [container] };
    },

    async refreshData() {
      // Clear all caches to force fresh data
      this.cachedData = {
        monthly: {},
        weekly: {},
        daily: {},
      };
      await this.fetchCalendarData();
    },

    handleDateSelect(selectInfo) {
      this.isEditMode = false;
      this.eventForm = {
        title: "",
        start: selectInfo.startStr.slice(0, 16),
        end: selectInfo.endStr.slice(0, 16),
        color: "#2196F3",
        allDay: selectInfo.allDay,
      };
      this.showModal = true;
    },

    // handleEventClick(clickInfo) {
    //   this.isEditMode = true;
    //   this.selectedEvent = clickInfo.event;
    //   this.eventForm = {
    //     title: clickInfo.event.title,
    //     start: this.formatDateForInput(clickInfo.event.start),
    //     end: this.formatDateForInput(
    //       clickInfo.event.end || clickInfo.event.start
    //     ),
    //     color: clickInfo.event.backgroundColor || "#2196F3",
    //     allDay: clickInfo.event.allDay,
    //   };
    //   this.showModal = true;
    // },

    handleEventDrop(info) {
      // console.log("Event moved:", info.event.title);
    },

    handleEventResize(info) {
      // console.log("Event resized:", info.event.title);
    },

    handleDatesSet(dateInfo) {
      // const firstDate = dateInfo.start;
      // this.fetchCalendarData(this.formatDate(firstDate));
      this.currentDateTitle = dateInfo.view.title;
    },

    handleSaveEvent() {
      const calendarApi = this.$refs.fullCalendar.getApi();

      if (this.isEditMode && this.selectedEvent) {
        this.selectedEvent.setProp("title", this.eventForm.title);
        this.selectedEvent.setStart(this.eventForm.start);
        this.selectedEvent.setEnd(this.eventForm.end);
        this.selectedEvent.setProp("backgroundColor", this.eventForm.color);
        this.selectedEvent.setAllDay(this.eventForm.allDay);
      } else {
        calendarApi.addEvent({
          id: String(Date.now()),
          title: this.eventForm.title,
          start: this.eventForm.start,
          end: this.eventForm.end,
          color: this.eventForm.color,
          allDay: this.eventForm.allDay,
        });
      }

      this.closeModal();
    },

    handleDeleteEvent() {
      if (this.selectedEvent) {
        this.selectedEvent.remove();
        this.closeModal();
      }
    },

    openAddEventModal() {
      this.isEditMode = false;
      const now = new Date();
      const startStr = this.formatDateForInput(now);
      const endDate = new Date(now.getTime() + 60 * 60 * 1000);

      this.eventForm = {
        title: "",
        start: startStr,
        end: this.formatDateForInput(endDate),
        color: "#2196F3",
        allDay: false,
      };
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.selectedEvent = null;
    },

    changeView(viewType) {
      const calendarApi = this.$refs.fullCalendar.getApi();
      calendarApi.changeView(viewType);
      this.currentView = viewType;

      // Fetch data for the new view type
      this.fetchCalendarData();
    },

    formatDateForInput(date) {
      if (!date) return "";
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    },
  },
};
</script>

<style scoped>
.calendar-app {
  width: 90%;
  height: 50%;
  margin: 0 auto;
}

@media (max-width: 1370px) {
  .calendar-app {
    width: 95%;
  }
}

.calendar-container-wrapper {
  height: 100%;
}

.calendar-wrapper {
  height: 63vh;
  overflow-y: scroll;
}

@media (max-width: 1600px) {
  .calendar-wrapper {
    height: 55vh;
  }
}

@media (min-height: 850px) {
  .calendar-wrapper {
    height: 61vh;
  }
}

@media (min-height: 900px) {
  .calendar-wrapper {
    height: 63vh;
  }
}

@media (min-height: 1050px) {
  .calendar-wrapper {
    height: 70vh;
  }
}

@media (max-width: 1440px) {
  .calendar-wrapper {
    height: 57vh;
  }
}

@media (max-width: 1370px) {
  .calendar-wrapper {
    height: 55vh;
  }
}

@media (max-width: 1280px) {
  .calendar-wrapper {
    height: 50vh;
  }
}

/* Mobile view */
@media (max-width: 768px) {
  .calendar-wrapper {
    height: calc(100vh - 120px);
  }
}

/* FullCalendar Customization */
::v-deep .fc-theme-standard td,
::v-deep .fc-theme-standard th {
  border-color: #e0e0e0;
}

::v-deep .fc-theme-standard .fc-scrollgrid {
  border-color: #e0e0e0;
}

::v-deep .fc-col-header-cell {
  padding: 12px 8px;
  font-weight: 600;
  font-size: 13px;
  color: #666;
  background: white;
  border-color: #e0e0e0;
}

::v-deep .fc-daygrid-day-number {
  padding: 8px;
  font-size: 14px;
  color: #1a1a1a;
  font-weight: 400;
}

::v-deep .fc-day-today {
  background-color: #f5f9ff !important;
}

::v-deep .fc-day-today .fc-daygrid-day-number {
  font-weight: 600;
  color: #2196f3;
}

::v-deep .fc-daygrid-day {
  min-height: 100px;
}

::v-deep .fc-daygrid-day-frame {
  height: 100px;
}

::v-deep .fc-daygrid-day-events {
  margin-top: 4px;
}

::v-deep .fc-event {
  margin: 0 !important;
  border: none !important;
}

::v-deep .fc-daygrid-event-harness {
  margin: 2px 4px !important;
}

/* Time grid (weekly and daily) cell heights */
::v-deep .fc-timegrid-slot {
  height: 75px !important;
  min-height: 75px !important;
}

::v-deep .fc-timegrid-slot-label {
  font-size: 12px;
  color: #666;
  vertical-align: top;
  padding-top: 4px;
}

/* Make time grid events more readable */
::v-deep .fc-timegrid-event {
  padding: 0 !important;
  margin: 0 !important;
  border: none !important;
  border-radius: 0 !important;
}

::v-deep .fc-timegrid-event .fc-event-main {
  height: 100%;
}

/* Increase column width for better readability */
::v-deep .fc-timegrid-axis {
  width: 60px !important;
}

/* Make the calendar taller for weekly/daily views */
::v-deep .fc-timeGridWeek-view,
::v-deep .fc-timeGridDay-view {
  min-height: 700px;
}

::v-deep .fc-timeGridWeek-view .fc-scrollgrid,
::v-deep .fc-timeGridDay-view .fc-scrollgrid {
  min-height: 700px;
}

::v-deep .fc-timegrid-event .fc-event-main-frame {
  height: 100%;
  display: flex;
  flex-direction: column;
}

::v-deep .fc-timegrid-event-harness {
  margin: 0 !important;
}

/* Custom event styling for time grid */
::v-deep .fc-timegrid .custom-event {
  margin: 0;
  border-radius: 0;
  font-size: 12px;
  color: white;
  font-weight: 500;
  white-space: pre-wrap;
  line-height: 1.4;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
}

.custom-event {
  cursor: default;
}

.btn-group .btn.active {
  background-color: #e9ecef;
  color: #212529;
  font-weight: 500;
}

@media (max-width: 768px) {
  .calendar-app {
    padding: 0;
  }
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
  color: #1a1a1a;
  transition: border-color 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #2196f3;
}
/* Make column headers clickable in week view */
::v-deep .fc-timeGridWeek-view .fc-col-header-cell {
  cursor: pointer;
  transition: background-color 0.2s;
}

::v-deep .fc-timeGridWeek-view .fc-col-header-cell:hover {
  background-color: #f0f0f0;
}

/* Make events clickable in week view */
::v-deep .fc-timeGridWeek-view .fc-event {
  cursor: pointer;
}

::v-deep .fc-timeGridWeek-view .fc-event:hover {
  opacity: 0.9;
}

/* Make column headers clickable in week view */
::v-deep .fc-timeGridWeek-view .fc-col-header-cell {
  cursor: pointer;
  transition: background-color 0.2s;
}

::v-deep .fc-timeGridWeek-view .fc-col-header-cell:hover {
  background-color: #f0f0f0;
}

/* Make events clickable in week view */
::v-deep .fc-timeGridWeek-view .fc-event {
  cursor: pointer;
}

::v-deep .fc-timeGridWeek-view .fc-event:hover {
  opacity: 0.9;
}

/* Make day cells clickable in month view */
::v-deep .fc-dayGridMonth-view .fc-daygrid-day {
  cursor: pointer;
  transition: background-color 0.1s;
}

::v-deep .fc-dayGridMonth-view .fc-daygrid-day:hover {
  background-color: #f9f9f9;
}

/* Make events clickable in month view */
::v-deep .fc-dayGridMonth-view .fc-event {
  cursor: pointer;
}

::v-deep .fc-dayGridMonth-view .fc-event:hover {
  opacity: 0.85;
}

::v-deep .fc-dayGridMonth-view .fc-daygrid-day-number {
  cursor: pointer;
}
/* Color Legend */
.calendar-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 24px;
  margin-top: 8px;
  padding: 12px 20px;
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 3px;
  flex-shrink: 0;
}

.legend-text {
  font-size: 13px;
  color: #555;
  font-weight: 400;
  line-height: 1.4;
}

@media (max-width: 768px) {
  .calendar-legend {
    gap: 12px;
    padding: 10px 14px;
    flex-direction: column;
    align-items: flex-start;
  }

  .legend-text {
    font-size: 12px;
  }
}
</style>
